import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useDateRangeStore, useDateRange } from '../../stores/useDateRangeStore';
import { DATE_RANGE_PRESETS, formatDateRangeDisplay, DateRange } from '../../utils/dateUtils';
import './DateRangeSelector.css';

/**
 * Date Range Selector Component
 * 
 * Professional date range selector using react-datepicker library.
 * Click the trigger button to open a popup with calendar and preset buttons.
 * 
 * Features:
 * - Visual calendar for date range selection
 * - Preset buttons: Today, Yesterday, Last 7 Days, Last 30 Days, MTD, Last Month, QTD, YTD
 * - Click-outside and Escape to close
 * - Dark theme styling
 */
const DateRangeSelector: React.FC = () => {
  const { rangeType, dateRange } = useDateRange();
  const { setPreset, setCustomRange } = useDateRangeStore();
  
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Parse date range into Date objects when it changes
  // Use local timezone to avoid off-by-one errors in the calendar
  useEffect(() => {
    if (dateRange.start_date) {
      const [year, month, day] = dateRange.start_date.split('-').map(Number);
      setStartDate(new Date(year, month - 1, day));
    }
    if (dateRange.end_date) {
      const [year, month, day] = dateRange.end_date.split('-').map(Number);
      setEndDate(new Date(year, month - 1, day));
    }
  }, [dateRange]);

  const handlePresetClick = (preset: typeof DATE_RANGE_PRESETS[0]) => {
    setPreset(preset.type);
    setIsOpen(false);
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    // If both dates are selected, apply the custom range
    if (start && end) {
      const range: DateRange = {
        start_date: formatDateForAPI(start),
        end_date: formatDateForAPI(end)
      };
      setCustomRange(range);
      setIsOpen(false);
    }
  };

  // Format date in local timezone to match what the user sees in the calendar
  const formatDateForAPI = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const togglePicker = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="date-range-selector">
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        className="date-range-trigger"
        onClick={togglePicker}
        aria-label="Select date range"
        aria-expanded={isOpen}
      >
        <svg 
          className="calendar-icon" 
          width="16" 
          height="16" 
          viewBox="0 0 16 16" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="2" y="3" width="12" height="11" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          <path d="M2 6h12" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M5 1v3M11 1v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <span className="selected-range-text">
          {formatDateRangeDisplay(dateRange, rangeType)}
        </span>
        <svg 
          className={`chevron-icon ${isOpen ? 'open' : ''}`}
          width="12" 
          height="12" 
          viewBox="0 0 12 12" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* DatePicker Popup */}
      {isOpen && (
        <div className="date-range-popup">
          <div className="popup-layout">
            {/* Left side: Quick Select buttons */}
            <div className="popup-sidebar">
              <h4 className="popup-section-title">Quick Select</h4>
              <div className="preset-buttons-vertical">
                {DATE_RANGE_PRESETS.map((preset) => (
                  <button
                    key={preset.type}
                    className={`preset-button-vertical ${rangeType === preset.type ? 'active' : ''}`}
                    onClick={() => handlePresetClick(preset)}
                    title={preset.label}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Right side: Calendar */}
            <div className="popup-main">
              <h4 className="popup-section-title">Custom Range</h4>
              <div className="datepicker-wrapper">
                <DatePicker
                  selected={startDate}
                  onChange={handleDateChange}
                  startDate={startDate}
                  endDate={endDate}
                  selectsRange
                  inline
                  maxDate={new Date()}
                  calendarClassName="dark-calendar"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overlay to close on click outside */}
      {isOpen && (
        <div 
          className="date-range-overlay" 
          onClick={() => setIsOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsOpen(false);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Close date picker"
        />
      )}
    </div>
  );
};

export default DateRangeSelector;
